import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { usePager } from "../components/Pagination";
import StatusBadge from "../components/StatusBadge";
import { ShieldCheck, CheckCircle2, XCircle, PauseCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog } from "./Suppliers";
import DeleteRequestButton from "../components/DeleteRequestButton";
import EditRequestButton from "../components/EditRequestButton";
import useSubmitGuard from "../lib/useSubmitGuard";
import { fmtQty } from "../lib/format";
import { fmtIST } from "../lib/time";
import { useLocation, useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_supply-control-20/artifacts/rvxe15j7_1.png";

export default function Quality() {
  const [rows, setRows] = useState([]);
  const [grns, setGrns] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [active, setActive] = useState(null);
  const [deviation, setDeviation] = useState(null);
  const [reportOf, setReportOf] = useState(null);

  const load = async () => {
    const [q, g, m] = await Promise.all([api.get("/quality"), api.get("/grns"), api.get("/materials")]);
    setRows(q.data); setGrns(g.data); setMaterials(m.data);
  };
  useEffect(() => { load(); }, []);

  const mat = (id) => materials.find((x) => x.id === id) || {};
  // Wave-G: id→{code,name} map for the Edit-with-approval JSON helper
  const materialsMap = React.useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, { code: m.code, name: m.name }])),
    [materials],
  );
  const grn = (id) => grns.find((g) => g.id === id) || {};

  // Live-Prod-Fix #3 round-trip — honor ?grn=<number> deep-link from the
  // GRN list ("QC ↗"): filter the table to just that GRN's inspection.
  const loc = useLocation();
  const nav = useNavigate();
  const grnFilter = new URLSearchParams(loc.search).get("grn");
  const displayRows = grnFilter ? rows.filter((q) => q.grn_number === grnFilter) : rows;

  // Wave-J pagination
  const { visibleRows: pagedRows, controls: pageControls } = usePager(displayRows, 10);

  return (
    <div data-testid="quality-page">
      <PageHeader title="Quality Inspection" subtitle="Inspector approves GRN · NG routes to Plant Head/Admin for deviation"
        helpText="Quality Inspector is the GRN approver. Each material in the GRN gets its own inspection. OK on all → auto stock-in. Any NG → Plant Head/Admin decides Accept-as-is, Conditional, or Reject."
      />
      <div className="p-8 space-y-6">
        {grnFilter && (
          <div className="bg-[#1565C0]/5 border border-[#1565C0]/30 rounded-sm px-4 py-2 flex items-center justify-between" data-testid="qc-grn-filter-banner">
            <div className="text-xs text-[#0D2B5E]">
              Showing inspection for GRN <span className="font-mono font-bold">{grnFilter}</span> ({displayRows.length} result{displayRows.length === 1 ? "" : "s"})
            </div>
            <button onClick={() => nav("/quality")} className="text-[10px] uppercase tracking-widest font-bold text-[#1565C0] hover:text-[#0D2B5E]" data-testid="qc-clear-grn-filter">Clear filter</button>
          </div>
        )}
        <div className="bg-white border border-[#EEEEEE] rounded-sm">
          <div className="px-5 py-3 border-b border-[#EEEEEE] flex items-center gap-2 bg-[#F5F5F5]">
            <ShieldCheck size={14}/>
            <div className="text-[10px] tracking-[0.2em] font-bold text-[#0D2B5E] uppercase">Quality Queue</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2">GRN No.</th>
                  <th className="text-right px-4 py-2"># Items</th>
                  <th className="text-left px-4 py-2">Inspector</th>
                  <th className="text-left px-4 py-2">Result</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((q, i) => {
                  const g = grn(q.grn_id);
                  return (
                    <tr key={q.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                      <td className="px-4 py-2 font-mono text-[#1565C0]">
                        <div className="flex items-center gap-2">
                          <span data-testid={`qc-grn-no-${q.grn_number}`}>{q.grn_number}</span>
                          {/* Live-Prod-Fix #3 — direct link to the GRN page scoped to this GRN */}
                          <a
                            href={`/grn?grn=${encodeURIComponent(q.grn_number)}`}
                            data-testid={`qc-open-grn-${q.grn_number}`}
                            title="Open linked GRN"
                            className="text-[9px] uppercase tracking-widest border border-[#1565C0] text-[#1565C0] px-1.5 py-0.5 rounded-sm hover:bg-[#1565C0] hover:text-white transition"
                          >GRN ↗</a>
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-right">{(g.items || []).length}</td>
                      <td className="px-4 py-2 text-xs">
                        <div>{q.inspector_name || "—"}</div>
                        {/* Wave-23 Ch-1: submission timestamp in IST */}
                        {q.inspected_at && <div className="text-[10px] text-[#0D2B5E]/50 font-mono" data-testid={`q-submitted-${q.grn_number}`}>Submitted {fmtIST(q.inspected_at)}</div>}
                      </td>
                      <td className="px-4 py-2 text-xs uppercase tracking-widest">{q.overall_result || "—"}</td>
                      <td className="px-4 py-2">
                        <StatusBadge status={q.status}/>
                        {/* Wave-23 Ch-1: approval (deviation/QC-ok) timestamp in IST */}
                        {q.deviation_at && <div className="text-[10px] text-[#0D2B5E]/50 font-mono mt-0.5" data-testid={`q-approved-${q.grn_number}`}>Approved {fmtIST(q.deviation_at)}</div>}
                      </td>
                      <td className="px-4 py-2 text-right space-x-1">
                        {q.status === "quality_pending" && (
                          <button onClick={() => setActive(q)} data-testid={`inspect-${q.grn_number}`} className="text-xs bg-[#1565C0] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold">Inspect</button>
                        )}
                        {q.status === "quality_rejected" && (
                          <button onClick={() => setDeviation(q)} data-testid={`deviation-${q.grn_number}`} className="text-xs bg-[#E65100] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold">Deviation</button>
                        )}
                        {q.inspected_at && (
                          <button onClick={() => setReportOf(q)} data-testid={`report-${q.grn_number}`} className="text-xs border border-[#1565C0] text-[#1565C0] px-2 py-1 rounded-sm uppercase tracking-wider font-bold inline-flex items-center gap-1"><FileText size={12}/>Report</button>
                        )}
                        {/* Wave-C #1+#2: Edit-with-approval — only after inspection submitted */}
                        {q.inspected_at && (
                          <span className="inline-block align-middle"><EditRequestButton
                            module="quality_inspection" record={q} label={q.grn_number}
                            materialsMap={materialsMap}
                            materials={materials}
                            fields={[
                              { key: "items", label: "Inspection Items + Samples", type: "items_qi" },
                            ]}
                            onSubmitted={load}/></span>
                        )}
                        <span className="inline-block align-middle"><DeleteRequestButton module="quality_inspection" record={q} label={q.grn_number} onDeleted={load}/></span>
                      </td>
                    </tr>
                  );
                })}
                {displayRows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-[#0D2B5E]/50">{grnFilter ? `No inspection found for GRN ${grnFilter}.` : "No quality tasks."}</td></tr>}
              </tbody>
            </table>
            {pageControls}
          </div>
        </div>
      </div>

      {active && <InspectionForm q={active} grn={grn(active.grn_id)} mat={mat} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }}/>}
      {deviation && <DeviationForm q={deviation} onClose={() => setDeviation(null)} onDone={() => { setDeviation(null); load(); }}/>}
      {reportOf && <ReportView q={reportOf} grn={grn(reportOf.grn_id)} mat={mat} onClose={() => setReportOf(null)}/>}
    </div>
  );
}

function valueOos(raw, spec_raw, rtype) {
  const r = String(raw ?? "").trim();
  if (!r) return false;
  if (rtype === "pass_fail") return !["pass", "ok", "yes", "true", "p"].includes(r.toLowerCase());
  if (rtype !== "numeric") return false;
  const numMatch = r.match(/-?\d+(?:\.\d+)?/);
  if (!numMatch) return false;
  const v = Number(numMatch[0]);
  if (!Number.isFinite(v)) return false;
  const spec = (spec_raw || "").trim().toLowerCase().replace(/\s/g, "");
  if (!spec) return false;
  for (const tok of ["±", "+/-", "+-"]) {
    if (spec.includes(tok)) {
      const re = new RegExp(`(-?\\d+(?:\\.\\d+)?)\\s*${tok.replace(/[+/-]/g, c => `\\${c}`)}\\s*(-?\\d+(?:\\.\\d+)?)`);
      const m = spec.match(re);
      if (m) { const b = Number(m[1]), t = Number(m[2]); return v < b - t || v > b + t; }
      return false;
    }
  }
  const cmps = [["≥", "ge"], [">=", "ge"], ["≤", "le"], ["<=", "le"], [">", "gt"], ["<", "lt"]];
  for (const [s, op] of cmps) {
    if (spec.includes(s)) {
      const re = new RegExp(`${s.replace(/[\\^$.*+?()[\]{}|]/g, "\\$&")}\\s*(-?\\d+(?:\\.\\d+)?)`);
      const m = spec.match(re);
      if (m) {
        const b = Number(m[1]);
        if (op === "ge") return v < b;
        if (op === "le") return v > b;
        if (op === "gt") return v <= b;
        if (op === "lt") return v >= b;
      }
      return false;
    }
  }
  const rng = spec.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  if (rng) {
    const lo = Number(rng[1]), hi = Number(rng[2]);
    if (hi >= lo) return v < lo || v > hi;
  }
  const nums = spec.match(/-?\d+(?:\.\d+)?/g);
  if (nums && nums.length === 1) {
    const target = Number(nums[0]);
    if (Number.isFinite(target)) return Math.abs(v - target) > 1e-6;
  }
  return false;
}

// Wave-23 Ch-2: mirrors backend _ng_threshold. size 5→2, 8→3, 10→3 etc.
function ngThreshold(sampleSize) {
  const raw = sampleSize * 0.25;
  const ceilRaw = Math.ceil(raw);
  return Math.abs(raw - ceilRaw) < 1e-9 ? ceilRaw + 1 : ceilRaw;
}

function paramFails(p) {
  const samples = Array.isArray(p.samples) ? p.samples : (p.result != null ? [p.result] : []);
  const oos = samples.filter((s) => valueOos(s, p.specification, p.result_type)).length;
  if (samples.length === 0) return false;
  return oos >= ngThreshold(samples.length);
}

function InspectionForm({ q, grn, mat, onClose, onDone }) {
  // Feb-2026 — submit guard prevents double-tap duplicates on tablets.
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [itemsResults, setItemsResults] = useState([]);

  useEffect(() => {
    (async () => {
      const out = [];
      for (const it of grn.items || []) {
        const r = await api.get(`/templates/by-material/${it.material_id}`);
        const tpl = r.data;
        const params = (tpl?.parameters || []).map((p) => {
          const size = Math.max(1, Math.min(20, +p.sample_size || 1));
          return {
            ...p,
            sample_size: size,
            // Wave-23 Ch-2: parameter-level samples array sized by sample_size.
            samples: Array.from({ length: size }, () => (p.result_type === "pass_fail" ? "pass" : "")),
          };
        });
        out.push({
          material_id: it.material_id,
          template_version: tpl?.version || 1,
          parameters: params,
          overall_result: "ok",
          inspection_qty: it.received_qty,
          sample_size: Math.max(5, Math.floor((it.received_qty || 10) / 10)),
          remarks: "",
        });
      }
      setItemsResults(out);
    })();
  }, [grn]);

  const updateItemRes = (idx, patch) => setItemsResults((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const updateParam = (itemIdx, paramIdx, patch) => setItemsResults((arr) => arr.map((it, i) =>
    i === itemIdx ? { ...it, parameters: it.parameters.map((p, pi) => pi === paramIdx ? { ...p, ...patch } : p) } : it
  ));

  // Compute auto-NG status per item (UX feedback; server is authoritative)
  const autoStatus = (it) => {
    const failed = (it.parameters || []).filter(paramFails);
    return failed.length ? { ng: true, names: failed.map((p) => p.name) } : { ng: false, names: [] };
  };

  // Auto-flip overall_result to "ng" whenever a param starts failing
  useEffect(() => {
    setItemsResults((arr) => arr.map((it) => {
      const a = autoStatus(it);
      if (a.ng && it.overall_result !== "ng") return { ...it, overall_result: "ng" };
      return it;
    }));
    // eslint-disable-next-line
  }, [JSON.stringify(itemsResults.map((x) => x.parameters))]);

  const submit = async () => {
    for (const it of itemsResults) {
      for (const p of it.parameters) {
        if (p.mandatory && (p.samples || []).some((s) => !String(s ?? "").trim())) {
          toast.error(`"${p.name}" — fill all ${p.samples.length} sample value(s)`); return;
        }
      }
    }
    try {
      await submitGuard(async (idemKey) => {
        await api.post(`/quality/${q.id}/submit`, { items: itemsResults.map((i) => ({
          material_id: i.material_id,
          parameters: i.parameters.map((p) => ({
            name: p.name, specification: p.specification, method: p.method,
            result_type: p.result_type, mandatory: p.mandatory, sample_size: p.sample_size,
            samples: p.samples,
            // Keep a legacy `result` (first sample) for any older consumers.
            result: (p.samples || [])[0] || "",
          })),
          overall_result: i.overall_result, inspection_qty: +i.inspection_qty,
          sample_size: +i.sample_size, remarks: i.remarks,
        })) }, { headers: { "Idempotency-Key": idemKey } });
      });
      toast.success("Inspection submitted");
      resetGuard();
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog title={`Inspection — ${q.grn_number}`} onClose={onClose} wide>
      {itemsResults.map((ir, idx) => {
        const m = mat(ir.material_id);
        const autoNg = autoStatus(ir);
        return (
          <div key={ir.material_id || `row-${idx}`} className="border border-[#EEEEEE] rounded-sm mb-4">
            <div className="px-4 py-2 bg-[#F5F5F5] flex items-center justify-between">
              <div className="text-sm font-bold text-[#0D2B5E]"><span className="font-mono text-[#1565C0]">{m.code}</span> — {m.name} <span className="text-[10px] text-[#0D2B5E]/50 ml-2">Tpl v{ir.template_version}</span>
                {autoNg.ng && <span className="ml-2 text-[10px] tracking-widest uppercase font-bold bg-[#C62828] text-white px-2 py-0.5 rounded-sm" data-testid={`auto-ng-${idx}`} title={`Failing: ${autoNg.names.join(", ")}`}>AUTO-NG</span>}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {["ok", "ng", "hold"].map((r) => (
                  <button key={r} onClick={() => updateItemRes(idx, { overall_result: r })} disabled={autoNg.ng && r !== "ng"} data-testid={`overall-${idx}-${r}`}
                    className={`px-3 py-1 text-xs rounded-sm uppercase tracking-widest font-bold border ${ir.overall_result === r ? (r === "ok" ? "bg-[#2E7D32] text-white border-[#2E7D32]" : r === "ng" ? "bg-[#C62828] text-white border-[#C62828]" : "bg-[#E65100] text-white border-[#E65100]") : "bg-white text-[#0D2B5E] border-[#EEEEEE]"} ${autoNg.ng && r !== "ng" ? "opacity-30 cursor-not-allowed" : ""}`}>
                    {r === "ok" ? <CheckCircle2 size={12} className="inline mr-1"/> : r === "ng" ? <XCircle size={12} className="inline mr-1"/> : <PauseCircle size={12} className="inline mr-1"/>}{r}
                  </button>
                ))}
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr><th className="text-left px-3 py-1">Parameter</th><th className="text-left px-3 py-1">Spec</th><th className="text-left px-3 py-1">Method</th><th className="text-left px-3 py-1">Samples · NG rule</th></tr>
              </thead>
              <tbody>
                {ir.parameters.map((p, pi) => {
                  const failed = paramFails(p);
                  const oosCount = (p.samples || []).filter((s) => valueOos(s, p.specification, p.result_type)).length;
                  const threshold = ngThreshold((p.samples || []).length || 1);
                  return (
                  <tr key={`${ir.material_id}-${p.name || pi}`} className={failed ? "bg-[#C62828]/10" : (pi % 2 === 0 ? "" : "bg-[#F5F5F5]/40")} data-testid={`param-row-${idx}-${pi}`}>
                    <td className="px-3 py-1 font-medium align-top pt-2">{p.name}{p.mandatory && <span className="text-[#C62828]">*</span>}{failed && <span className="ml-2 text-[9px] tracking-widest uppercase font-bold text-[#C62828]">FAIL</span>}</td>
                    <td className="px-3 py-1 font-mono text-xs align-top pt-2">{p.specification}</td>
                    <td className="px-3 py-1 text-xs align-top pt-2">{p.method}</td>
                    <td className="px-3 py-1 align-top">
                      {/* Wave-23 Ch-2: N sample inputs per parameter.
                          NOTE — sample arrays are PRIMITIVE position-bound
                          values (sample #1, #2, …). Using array index as
                          React key is semantically correct here: the slot
                          IS the identity, and we never reorder/filter. */}
                      <div className="flex flex-wrap gap-1">
                        {(p.samples || []).map((s, si) => {
                          const oos = valueOos(s, p.specification, p.result_type);
                          return p.result_type === "pass_fail" ? (
                            <select key={si} value={s} onChange={(e) => updateParam(idx, pi, { samples: p.samples.map((x, xi) => xi === si ? e.target.value : x) })} data-testid={`param-${idx}-${pi}-s${si}`} className={`border px-1.5 py-1 text-xs rounded-sm ${oos ? "border-[#C62828] text-[#C62828] font-bold" : "border-[#EEEEEE]"}`}>
                              <option value="pass">P</option><option value="fail">F</option>
                            </select>
                          ) : (
                            <input key={si} value={s} placeholder={`#${si+1}`} onChange={(e) => updateParam(idx, pi, { samples: p.samples.map((x, xi) => xi === si ? e.target.value : x) })} data-testid={`param-${idx}-${pi}-s${si}`} className={`w-16 border px-1.5 py-1 text-xs rounded-sm font-mono ${oos ? "border-[#C62828] text-[#C62828] font-bold" : "border-[#EEEEEE]"}`}/>
                          );
                        })}
                      </div>
                      <div className="text-[9px] text-[#0D2B5E]/60 mt-0.5 font-mono">
                        OOS {oosCount}/{(p.samples || []).length} · NG if ≥ {threshold}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-3 py-2 grid grid-cols-3 gap-3 border-t border-[#EEEEEE]">
              <div><label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Insp Qty</label><input type="number" step="any" value={ir.inspection_qty} onChange={(e) => updateItemRes(idx, { inspection_qty: e.target.value })} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/></div>
              <div><label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Sample</label><input type="number" step="any" value={ir.sample_size} onChange={(e) => updateItemRes(idx, { sample_size: e.target.value })} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono"/></div>
              <div><label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Remarks</label><input value={ir.remarks} onChange={(e) => updateItemRes(idx, { remarks: e.target.value })} className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm"/></div>
            </div>
          </div>
        );
      })}
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
        <button onClick={submit} disabled={submitBusy} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-inspection-btn">{submitBusy ? "Submitting…" : "Submit Inspection"}</button>
      </div>
    </Dialog>
  );
}

function DeviationForm({ q, onClose, onDone }) {
  const [decision, setDecision] = useState("accept_as_is");
  const [remark, setRemark] = useState("");
  const submit = async () => {
    if (!remark.trim()) { toast.error("Remark required"); return; }
    try {
      await api.post(`/quality/${q.id}/deviation`, { decision, remark });
      toast.success("Deviation decision recorded");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog title={`NG Deviation — ${q.grn_number}`} onClose={onClose}>
      <div className="text-sm mb-3 text-[#C62828] border border-[#C62828]/30 bg-[#C62828]/5 p-3 rounded-sm">
        Material rejected in QC. <strong>Plant Head / Admin</strong> decides outcome.
      </div>
      <div className="space-y-2 mb-3">
        {[["accept_as_is", "Accept as-is", "Material accepted despite NG; flagged in stock."],
          ["conditional", "Conditional Accept", "Accepted with usage restrictions."],
          ["reject", "Full Rejection", "Return to supplier; no stock entry."]].map(([val, label, hint]) => (
          <label key={val} className={`flex items-start gap-3 border px-3 py-2 rounded-sm cursor-pointer ${decision === val ? "border-[#1565C0] bg-[#1565C0]/5" : "border-[#EEEEEE]"}`}>
            <input type="radio" checked={decision === val} onChange={() => setDecision(val)} data-testid={`dev-${val}`}/>
            <div>
              <div className="text-sm font-medium text-[#0D2B5E]">{label}</div>
              <div className="text-xs text-[#0D2B5E]/60">{hint}</div>
            </div>
          </label>
        ))}
      </div>
      <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Remark *</label>
      <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3} data-testid="deviation-remark" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onClose} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
        <button onClick={submit} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="submit-deviation-btn">Submit Decision</button>
      </div>
    </Dialog>
  );
}

function ReportView({ q, grn, mat, onClose }) {
  return (
    <Dialog title={`Inspection Report — ${q.grn_number}`} onClose={onClose} wide>
      <div className="print-area">
        <div className="flex items-center justify-between border-b-4 border-[#1565C0] pb-3 mb-5">
          <img src={LOGO_URL} alt="Supplar" className="h-12 object-contain"/>
          <div className="text-right text-xs font-mono">
            <div>GRN: <span className="text-[#1565C0]">{q.grn_number}</span></div>
            <div>Date: {fmtIST(q.inspected_at)?.slice(0, 10)}</div>
            <div>Inv: {grn.invoice_no || "—"}</div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 mb-4 text-sm">
          <KV k="Inspector" v={q.inspector_name}/>
          <KV k="Inspected (IST)" v={fmtIST(q.inspected_at)}/>
          <KV k="Overall" v={(q.overall_result || "").toUpperCase()}/>
          <KV k="Items" v={(q.items || []).length}/>
        </div>
        {(q.items || []).map((it, i) => {
          const m = mat(it.material_id) || {};
          return (
            <div key={it.material_id || `item-${i}`} className="mb-4">
              <div className="bg-[#F5F5F5] px-3 py-2 text-sm font-bold border border-[#EEEEEE] flex items-center justify-between">
                <div><span className="font-mono text-[#1565C0]">{m.code}</span> — {m.name}
                  <span className="text-xs text-[#0D2B5E]/60 ml-2">Insp Qty {fmtQty(it.inspection_qty)} · Sample {fmtQty(it.sample_size)}</span>
                </div>
                <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-sm font-bold text-white ${it.overall_result === "ok" ? "bg-[#2E7D32]" : it.overall_result === "ng" ? "bg-[#C62828]" : "bg-[#E65100]"}`}>{(it.overall_result || "").toUpperCase()}</span>
              </div>
              <table className="w-full text-sm border-x border-b border-[#EEEEEE] table-auto">
                <thead className="text-[10px] uppercase tracking-widest text-[#0D2B5E]/60 bg-[#F5F5F5]">
                  <tr>
                    <th className="text-left px-3 py-1" style={{ minWidth: "100px" }}>Parameter</th>
                    <th className="text-left px-3 py-1" style={{ minWidth: "100px" }}>Spec</th>
                    <th className="text-left px-3 py-1" style={{ minWidth: "80px" }}>Method</th>
                    <th className="text-left px-3 py-1" style={{ minWidth: "200px" }}>Samples</th>
                    <th className="text-center px-3 py-1" style={{ width: "60px" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(it.parameters || []).map((p, pi) => {
                    const failed = paramFails(p);
                    const samples = Array.isArray(p.samples) && p.samples.length ? p.samples : (p.result ? [p.result] : []);
                    return (
                      <tr key={`${it.material_id}-${p.name || pi}`} className={pi % 2 === 0 ? "" : "bg-[#F5F5F5]/40"} data-testid={`report-param-${pi}`}>
                        <td className="px-3 py-1 font-medium align-top whitespace-normal border-r border-[#EEEEEE]">{p.name}{p.mandatory && <span className="text-[#C62828]">*</span>}</td>
                        <td className="px-3 py-1 font-mono text-xs align-top whitespace-normal break-words border-r border-[#EEEEEE]">{p.specification}</td>
                        <td className="px-3 py-1 text-xs align-top whitespace-normal break-words border-r border-[#EEEEEE]">{p.method}</td>
                        <td className="px-2 py-1 align-top border-r border-[#EEEEEE]">
                          {samples.length > 0 ? (
                            <table className="w-full text-xs border-collapse" data-testid={`samples-table-${pi}`}>
                              <thead>
                                <tr>
                                  {samples.map((_, si) => (
                                    <th key={si} className="text-[9px] uppercase tracking-widest text-[#0D2B5E]/50 font-bold border border-[#EEEEEE] bg-[#F5F5F5] px-1.5 py-0.5">S{si + 1}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {samples.map((s, si) => {
                                    const oos = valueOos(s, p.specification, p.result_type);
                                    return (
                                      <td key={si} className={`font-mono text-center border border-[#EEEEEE] px-1.5 py-0.5 ${oos ? "text-[#C62828] bg-[#C62828]/10 font-bold" : "text-[#0D2B5E]"}`}>
                                        {s ?? "—"}
                                      </td>
                                    );
                                  })}
                                </tr>
                              </tbody>
                            </table>
                          ) : <span className="text-[#0D2B5E]/40 text-xs">—</span>}
                        </td>
                        <td className="px-3 py-1 text-center align-top">
                          <span className={`inline-block text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-sm text-white ${failed ? "bg-[#C62828]" : "bg-[#2E7D32]"}`}>{failed ? "NG" : "OK"}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {it.remarks && <div className="text-xs px-3 py-1 border-x border-b border-[#EEEEEE] bg-white"><strong>Remarks:</strong> {it.remarks}</div>}
            </div>
          );
        })}
        {q.deviation_decision && <div className="mt-3 text-sm border-l-4 border-[#E65100] pl-3"><span className="text-[10px] tracking-widest uppercase font-bold text-[#E65100]">Deviation [{q.deviation_decision}] by {q.deviation_by}: </span>{q.deviation_remark}</div>}
        <div className="mt-8 grid grid-cols-2 gap-8">
          <div><div className="text-xs text-[#0D2B5E]/60 border-t border-black pt-1">Inspector ({q.inspector_name})</div></div>
          <div><div className="text-xs text-[#0D2B5E]/60 border-t border-black pt-1">Plant Head / Managing Director</div></div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6 no-print">
        <button onClick={onClose} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Close</button>
        <button onClick={async () => {
          try {
            const r = await api.get(`/quality/${q.id}/report.pdf`, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `inspection_${q.grn_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch (e) {
            // axios with responseType=blob returns Blob even on error → parse it manually
            let detail = "PDF generation failed";
            try {
              const data = e?.response?.data;
              if (data instanceof Blob) {
                const text = await data.text();
                try { detail = JSON.parse(text)?.detail || text; } catch { detail = text || detail; }
              } else if (data) {
                detail = formatApiErrorDetail(data?.detail) || JSON.stringify(data);
              }
            } catch { /* fall through */ }
            toast.error(typeof detail === "string" ? detail : "PDF generation failed");
          }
        }} className="px-4 py-2 bg-[#C62828] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="server-pdf-btn">Download PDF</button>
        <button onClick={() => window.print()} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="print-report-btn">Print</button>
      </div>
    </Dialog>
  );
}

function KV({ k, v }) {
  return (
    <div className="border-l-2 border-[#1565C0] pl-3">
      <div className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/50 font-bold">{k}</div>
      <div className="text-sm font-medium text-[#0D2B5E]">{v}</div>
    </div>
  );
}
